/**
 * Human-readable label for a conditional path group.
 * Voluum often leaves `name` empty; the panel UI infers a title from conditions
 * (e.g. country). We mirror that so the card title matches expectations.
 */

import type {
  VoluumPathGroupConditions,
  VoluumPathGroupUpdate,
} from '@trafficpulse/shared';

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function countryCodesFromGroupConditions(
  c: VoluumPathGroupConditions,
): string[] {
  const country = asRecord(c.country);
  const list = country?.countries;
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (typeof item === 'string') return item.trim().toUpperCase();
      const r = asRecord(item);
      if (!r) return '';
      const code = r.code ?? r.id;
      return typeof code === 'string'
        ? code.trim().toUpperCase()
        : '';
    })
    .filter((x) => x.length > 0);
}

function formatCountryCodes(codes: readonly string[]): string {
  if (codes.length === 0) return '';
  try {
    const dn = new Intl.DisplayNames(['en-GB'], { type: 'region' });
    const names = codes.map((code) => dn.of(code) ?? code);
    if (names.length === 1) return names[0] ?? '';
    const head = names.slice(0, 2).join(', ');
    const rest = names.length - 2;
    return rest > 0 ? `${head} (+${rest})` : head;
  } catch {
    return codes.join(', ');
  }
}

export function getPathGroupDisplayLabel(group: VoluumPathGroupUpdate): string {
  const raw = group.name?.trim() ?? '';
  if (raw.length > 0 && raw !== 'Group') return raw;
  const inferred = formatCountryCodes(
    countryCodesFromGroupConditions(group.conditions),
  );
  if (inferred.length > 0) return inferred;
  return 'Unnamed group';
}
